import { toast, ToastContent, ToastOptions } from 'react-toastify'
import * as React from 'react'
import { ReactElement, useEffect, useMemo, useState } from 'react'
import {
    DocumentQueue24Regular,
    ErrorCircle24Regular,
    HourglassHalf24Regular,
    Info24Regular,
    Warning24Regular,
} from '@fluentui/react-icons'
import useAxios from '../hooks/useAxios'
import { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios'
import { normalizeAxiosError, NormalizedError } from '../reducers/errorReducer'
import { useQueue } from './useQueue'
import { StatefulMessage } from './azurewebsocket/useProcessMessage'
import { Queue } from '../context/QueueContext'

export enum QueueStatus {
    Dispatch = 'DISPATCH',
    Duplicate = 'DUPLICATE',
    Queued = 'QUEUED',
    Delayed = 'DELAYED',
    Cancelled = 'CANCELLED',
    Finished = 'FINISHED',
    Failed = 'FAILED',
}

// The data that applies to the whole chain of events.
export type QueueContextData<RequestT, StatusT, ContextT> = ContextT &
    Pick<AxiosRequestConfig<RequestT>, 'baseURL' | 'url' | 'method' | 'data'> & {
        // Mapper to extract the Azure SB message ID from the dispatch request.
        mapDispatchResponse: (response: AxiosResponse<unknown>) => any
        // Any status callbacks that are registered by the dispatcher.
        callbacks?: QueueCallbacks<RequestT, StatusT, ContextT>
        // The controller to emit an abortion signal to outgoing requests to the backend that have not yet been enqueued.
        abortController?: AbortController
        // A custom title to show in toaster messages that represents the document that's being generated.
        title?: ReactElement | string
    }

// The data that is specific for a given status transition and will not persist through the whole chain of events.
// The StatusT is optional, because not every status will include it.
export type QueueStatusData<StatusT = {}> = StatusT & {
    current?: QueueStatus
    error?: Pick<NormalizedError, 'message' | 'trace'>
}

export type QueueData<RequestT, StatusT, ContextT> = {
    delayTimer?: NodeJS.Timeout
    context: QueueContextData<RequestT, StatusT, ContextT>
    status: QueueStatusData<StatusT>
}

// Callbacks are called after updates.
export type QueueCallbacks<RequestT, StatusT, ContextT> = {
    [key in QueueStatus]?: (data: QueueData<RequestT, StatusT, ContextT> | undefined) => void
}

export const defaultToastOptions: Omit<ToastOptions, 'toastId'> = {
    type: 'info',
    icon: <Info24Regular />,
    autoClose: 30000,
    closeOnClick: false,
    pauseOnHover: false,
}

export const ToastOptionsMap = new Map<QueueStatus, Omit<ToastOptions, 'toastId'>>([
    [QueueStatus.Duplicate, { type: 'error', icon: <Warning24Regular /> }],
    [QueueStatus.Queued, { icon: <DocumentQueue24Regular /> }],
    [QueueStatus.Delayed, { icon: <HourglassHalf24Regular /> }],
    [QueueStatus.Cancelled, { type: 'success' }],
    [QueueStatus.Finished, { type: 'success' }],
    [QueueStatus.Failed, { type: 'error', icon: <ErrorCircle24Regular /> }],
])

//Signature of the onCancel method.
export type QueueStateCancel<StatusT = {}> = {
    (
        queue: Queue,
        transition: QueueStateTransition<StatusT>,
        requestId: string,
        graceful: boolean,
        status?: QueueStatusData<StatusT>,
    ): void
}

//Signature of the transition method.
export type QueueStateTransition<StatusT = {}> = {
    (
        requestId: string,
        next: QueueStatus,
        status?: Partial<QueueStatusData<StatusT>> | Pick<QueueStatusData, 'error'>,
    ): void
}

// This handler is a thin layer between the QueueContext and any functionality in need of a QueueContext. It handles
// state changes and allows the user to cancel the operation, if supported.
// RequestT can be any type.
// StatusT can be any type but will automatically gain the properties of QueueStatusData.
// ContextT can be any type but will automatically gain the properties of QueueContextData.
export default function useQueueStateHandler<RequestT, StatusT = {}, ContextT = {}>(
    renderToastComponent: (props) => ToastContent<any>,
    onCancel?: QueueStateCancel<StatusT>,
) {
    const axios = useAxios()
    const queue = useQueue()
    const [requestId, setRequestId] = useState<string | undefined>()
    const [updated, setUpdated] = useState(new Date())
    const data = useMemo<QueueData<RequestT, StatusT, ContextT> | undefined>(() => queue.get(requestId), [updated])
    const status = data?.status?.current

    useEffect(() => {
        if (!requestId || !status) {
            return
        }

        if ([QueueStatus.Failed, QueueStatus.Finished].includes(status)) {
            queue.remove(requestId)
        }

        if (status === QueueStatus.Cancelled) {
            cancel(queue, transition, requestId, true, data.context.abortController, data.status)
        }

        if (status === QueueStatus.Queued) {
            // Set a timer to keep track if we transition out of the queued status within a reasonable amount of time.
            const delayTimer = setTimeout(() => transition(requestId, QueueStatus.Delayed), 30000)
            queue.update<QueueData<RequestT, StatusT, ContextT>>(requestId, { ...data, delayTimer })
        }

        // Any status transition suffices to cancel the delay timer, except when we just set it.
        if (data.delayTimer && status !== QueueStatus.Queued) {
            clearTimeout(data.delayTimer)
        }

        const options: ToastOptions<QueueData<RequestT, StatusT, ContextT>> = {
            toastId: requestId,
            ...defaultToastOptions,
            ...ToastOptionsMap.get(status),
            data,
        }

        if (toast.isActive(requestId)) {
            toast.update(requestId, {
                ...options,
                render: props => renderToastComponent(props),
            })
        } else {
            toast(props => renderToastComponent(props), options)
        }

        // Run any callbacks that were registered by the dispatcher.
        data.context.callbacks?.[status]?.(data)
    }, [updated])

    const transition = (
        requestId: string,
        next: QueueStatus,
        status?: Partial<QueueStatusData<StatusT>> | Pick<QueueStatusData, 'error'>,
    ) => {
        if (!queue.has(requestId)) {
            return
        }
        const data = queue.get<QueueData<RequestT, StatusT, ContextT>>(requestId)
        // Update the queue with the new data.
        queue.update(requestId, {
            // The context will never change after initialization.
            context: { ...data.context },
            // The status will expand and build on top of the previous status.
            status: { ...data.status, current: next, ...(status || {}) },
        } as QueueData<RequestT, StatusT, ContextT>)

        setRequestId(requestId)
        // This will make sure we always make the transition, even when the status has not changed, because it will
        // still force a rerender of the toaster and event listeners.
        setUpdated(new Date())
    }

    const cancel = (
        queue: Queue,
        transition: QueueStateTransition,
        requestId: string,
        graceful: boolean,
        abortController?: AbortController,
        status?: QueueStatusData<StatusT>,
    ) => {
        // Broadcast an abort message to the current request if the message has not yet been added to the queue.
        abortController?.abort()

        if (!graceful) {
            // Directly remove from the queue if we're not waiting for a delete request to finish.
            queue.remove(requestId)
        }

        onCancel?.(queue, transition, requestId, graceful, status)
    }

    const dispatch = (requestId: string, context: QueueContextData<RequestT, StatusT, ContextT>, restart?: boolean) => {
        // If the user has elected to force a restart, we'll remove the current request from the queue and broadcast
        // a cancellation signal to the server.
        if (restart && cancel) {
            const data = queue.get(requestId) as QueueData<RequestT, StatusT, ContextT>
            // We're calling cancel() before the state has been populated by transition(), because we're starting all
            // over, and we're not interested in preserving the history.
            cancel(queue, transition, requestId, false, context.abortController, data?.status)
        }

        // Filter out duplicate requests.
        if (queue.has(requestId)) {
            // Show an error message that the request is duplicated.
            return transition(requestId, QueueStatus.Duplicate)
        }
        const abortController = new AbortController()
        queue.push(requestId, { context: { ...context, abortController } })
        transition(requestId, QueueStatus.Dispatch)

        axios
            .request({
                url: context.url,
                baseURL: context.baseURL || undefined,
                method: context.method || 'POST',
                data: { ...context.data, state: requestId } as StatefulMessage<RequestT>,
                headers: { SuppressErrors: true },
                signal: abortController.signal,
            })
            .then(
                (response: AxiosResponse) => {
                    transition(requestId, QueueStatus.Queued, context.mapDispatchResponse(response))
                },
                (response: AxiosError) => {
                    const error = normalizeAxiosError(response)
                    transition(requestId, QueueStatus.Failed, {
                        error: {
                            message: error?.message,
                            trace: error?.trace,
                        },
                    })
                },
            )
    }

    return { transition, dispatch }
}
